import { useGoogleLogin } from "@react-oauth/google";
import { useEffect, useMemo, useState } from "react";

import { deriveMainAppUrl, getAuthReturnUrl, isLogoutRequest, resolveApiDomain } from "./app-domains";
import {
  AUTH_STORAGE_KEY,
  clearAuthSession,
  createSessionHandoffUrl,
  isUsableAuthSession,
  loadAuthSession,
  loginWithGoogle,
  refreshAuthSession,
  storeAuthSession,
  type AuthSession,
} from "./session";
import nazareLogoUrl from "../../app/web/assets/lg.png";

export function AuthApplication() {
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const isLogout = isLogoutRequest(window.location.href);
  const result = useMemo(() => {
    try {
      const requestedReturnUrl = getAuthReturnUrl(window.location.href);
      return {
        returnUrl:
          requestedReturnUrl ??
          new URL(deriveMainAppUrl(window.location.origin, import.meta.env.VITE_NAZARE_APP_URL)),
        hasRequestedReturnUrl: Boolean(requestedReturnUrl),
        error: null,
      };
    } catch (error) {
      return {
        returnUrl: null,
        hasRequestedReturnUrl: false,
        error: error instanceof Error ? error.message : "The return URL is invalid.",
      };
    }
  }, []);

  const apiDomain = useMemo(
    () => resolveApiDomain(window.location.origin, import.meta.env.VITE_NAZARE_API_DOMAIN),
    [],
  );

  const handoffSession = (session: AuthSession) => {
    if (!result.returnUrl) return;
    window.location.assign(createSessionHandoffUrl(result.returnUrl, session));
  };

  // A deliberate sign-out: drop this app's own session — the thing that let a
  // return here silently hand a fresh one back to the app that just logged out —
  // then bounce to return_to (or the main app) with no session left to hand off.
  useEffect(() => {
    if (!isLogout) return;
    clearAuthSession(window.localStorage);
    if (result.returnUrl) window.location.replace(result.returnUrl.href);
  }, [isLogout, result.returnUrl]);

  useEffect(() => {
    if (isLogout || !result.hasRequestedReturnUrl) return;
    const existing = loadAuthSession(window.localStorage);
    if (!existing) return;
    if (isUsableAuthSession(existing)) {
      handoffSession(existing);
      return;
    }

    setIsLoading(true);
    void refreshAuthSession(existing, apiDomain)
      .then((session) => handoffSession(storeAuthSession(window.localStorage, session)))
      .catch(() => {
        window.localStorage.removeItem(AUTH_STORAGE_KEY);
        setIsLoading(false);
      });
  }, [apiDomain, isLogout, result.hasRequestedReturnUrl, result.returnUrl]);

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async ({ code }) => {
      try {
        const session = await loginWithGoogle(code, apiDomain, window.location.origin);
        handoffSession(storeAuthSession(window.localStorage, session));
      } catch {
        setLoginError("Sign-in failed. Please try again.");
        setIsLoading(false);
      }
    },
    onError: () => {
      setLoginError("Sign-in failed. Please try again.");
      setIsLoading(false);
    },
    onNonOAuthError: () => setIsLoading(false),
  });

  if (result.error) {
    return (
      <main className="status-page" role="alert">
        <p>{result.error}</p>
      </main>
    );
  }

  if (isLogout) {
    return (
      <main className="status-page" aria-label="Signing out">
        <span className="spinner" />
      </main>
    );
  }

  if (!result.returnUrl) {
    return (
      <main className="status-page" aria-label="Loading">
        <span className="spinner" />
      </main>
    );
  }

  const legalOrigin = deriveMainAppUrl(window.location.origin, import.meta.env.VITE_NAZARE_APP_URL);

  return (
    <main className="login-page">
      <section className="brand-panel" aria-label="Nazare">
        <div className="brand-name"><img alt="" className="brand-logo" src={nazareLogoUrl} /> NAZARE</div>
      </section>
      <section className="login-panel">
        <div className="login-card">
          <h1>Nazare</h1>
          {loginError && <p role="alert">{loginError}</p>}
          <button
            className="login-button"
            disabled={isLoading}
            onClick={() => {
              setIsLoading(true);
              googleLogin();
            }}
            type="button"
          >
            {isLoading && <span className="button-spinner" />}
            Sign in with Google
          </button>
          <p className="legal">
            By clicking continue, you agree to our <a href={`${legalOrigin}/terms`}>Terms of Service</a> and{" "}
            <a href={`${legalOrigin}/privacy`}>Privacy Policy</a>.
          </p>
        </div>
      </section>
      <span className="version">v{import.meta.env.VITE_APP_VERSION ?? "dev"}</span>
    </main>
  );
}
